import React, { useEffect, useRef, useState } from 'react';
import { StyledCurrencyInput, type CurrencyMaskedInputVariant } from './styles';

interface CurrencyMaskedInputProps {
  name: string;
  value: number | null;
  onChange: (value: number, rawValue: string) => void;
  id?: string;
  placeholder?: string;
  variant?: CurrencyMaskedInputVariant;
  disabled?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

function CurrencyMaskedInput({
  name,
  value,
  onChange,
  id,
  placeholder,
  variant = 'default',
  disabled = false,
  onKeyDown
}: CurrencyMaskedInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [innerValue, setInnerValue] = useState<string>(
    value ? value.toString() : ''
  );
  const [maskedValue, setMaskedValue] = useState<string>(
    value
      ? value
          .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          .replace(',00', '')
      : ''
  );
  const [didChangeBefore, setDidChangeBefore] = useState(false);

  useEffect(() => {
    const nextInner = value ? value.toString() : '';
    const nextMasked = value
      ? value
          .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          .replace(',00', '')
      : '';

    setInnerValue(nextInner);
    setMaskedValue(nextMasked);
  }, [value]);

  const setCaret = (position: number) => {
    requestAnimationFrame(() => {
      const element = inputRef.current;
      if (!element) {
        return;
      }
      element.setSelectionRange(position, position);
    });
  };

  const handleBeforeChange = (event: React.InputEvent<HTMLInputElement>) => {
    if (event.data !== '.') {
      setDidChangeBefore(false);
      return;
    }

    const {
      selectionStart,
      selectionEnd,
      value: currentValue
    } = event.currentTarget;

    const futureValue =
      currentValue.slice(0, selectionStart || 0) +
      ',' +
      currentValue
        .slice(selectionEnd || 0)
        .replace(',', '')
        .slice(0, 2);

    setMaskedValue(futureValue);
    setDidChangeBefore(true);
    setCaret((selectionStart || 0) + 1);
  };

  const handleValueChange = (nextValue: string | undefined) => {
    let valueText = nextValue ?? '';

    if (didChangeBefore && maskedValue) {
      valueText = maskedValue.replace(/[^\d,]/g, '');
    }

    setInnerValue(valueText);

    const numericValue = valueText
      ? parseFloat(valueText.replace(/\./g, '').replace(',', '.'))
      : 0;

    onChange(numericValue, valueText);
  };

  return (
    <StyledCurrencyInput
      ref={inputRef}
      id={id}
      name={name}
      value={innerValue}
      placeholder={placeholder}
      variantType={variant}
      allowNegativeValue={false}
      intlConfig={{ locale: 'pt-BR', currency: 'BRL' }}
      decimalsLimit={2}
      disabled={disabled}
      onBeforeInput={handleBeforeChange}
      onValueChange={handleValueChange}
      onKeyDown={onKeyDown}
    />
  );
}

export default CurrencyMaskedInput;
